/*
  Renders text strings from the HIH REST API that contain inline markup.

  Backend ships strings in a format shared with native apps:
    - \n  → line break
    - <u>…</u>  → underlined word/phrase

  Apps render the markup themselves. Web converts it to JSX here.
*/

import React from 'react';
import Underline from '@/components/base/Underline';

const UNDERLINE_PATTERN = /<u>(.*?)<\/u>/g;

interface RenderInlineMarkupOptions {
  underlineClassName?: string;
}

function renderUnderlines(
  line: string,
  lineIndex: number,
  underlineClassName?: string,
): React.ReactNode[] {
  const segments = line.split(UNDERLINE_PATTERN);
  return segments.map((segment, segmentIndex) => {
    const key = `${lineIndex}-${segmentIndex}`;
    const isUnderlined = segmentIndex % 2 === 1;
    if (isUnderlined) {
      return (
        <Underline key={key} variation="singleLine" className={underlineClassName}>
          {segment}
        </Underline>
      );
    }
    return <React.Fragment key={key}>{segment}</React.Fragment>;
  });
}

export function renderInlineMarkup(
  input: string,
  options?: RenderInlineMarkupOptions,
): React.ReactNode[] {
  const lines = input.split('\n');
  return lines.flatMap((line, lineIndex) => {
    const rendered = renderUnderlines(line, lineIndex, options?.underlineClassName);
    if (lineIndex < lines.length - 1) {
      return [...rendered, <br key={`br-${lineIndex}`} />];
    }
    return rendered;
  });
}
